import { Button, CircularProgress, Chip } from '@mui/material'
import { useNavigate, useParams } from 'react-router-dom'
import FilterListIcon from '@mui/icons-material/FilterList'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import { TextField } from '@mui/material'
import { DataGrid, GridColDef } from '@mui/x-data-grid'
import { useState } from 'react'
import { ReactComponent as Search } from 'assets/icons/search.svg'
import { useOrderRequests } from 'data/orders'
import ErrorFallback from 'components/ErrorFallback'
import EmptyState from 'components/EmptyState'
import PageTransition from 'components/PageTransition'
import { formatCurrency } from 'utils'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import { Refresh } from 'components/PullToRefresh'

export function Cart(props: { next?: (id: string) => void }) {
  const { id: pharmacyId } = useParams()
  const navigate = useNavigate()
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  })
  const {
    data: orders,
    isLoading,
    isError,
    refetch,
  } = useOrderRequests(
    ['initiated', 'awaiting approval'],
    paginationModel.page * paginationModel.pageSize,
    paginationModel.pageSize,
    pharmacyId
  )

  const columns: GridColDef[] = [
    {
      field: 'payment_id',
      headerName: 'Payment ID',
      flex: 1,
      sortable: false,
      headerClassName: 'pl-2',
    },
    {
      field: 'payment',
      headerName: 'Payment',
      flex: 1,
      sortable: false,
      headerClassName: 'pl-2',
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 1,
      sortable: false,
      renderCell: ({ row }) => {
        return (
          <div key={row.id}>
            <Chip label={row?.status} color={row.status === 'successful' ? 'success' : 'primary'} />
          </div>
        )
      },
    },
  ]

  const rows =
    orders?.items?.map((item) => {
      return {
        id: item.id,
        payment_id: item.id,
        payment: formatCurrency(
          item.total_shipping_cost
            ? item.total_order_cost + item.total_shipping_cost
            : item.total_order_cost
        ),
        status: item.request_status,
      }
    }) ?? []

  return (
    <>
      <PageTransition show>
        <Refresh refetch={refetch}>
          <div className="lg:hidden overflow-hidden">
            {isLoading ? (
              <div className="grid place-items-center mt-40">
                <CircularProgress />
              </div>
            ) : isError ? (
              <ErrorFallback
                message="Something went wrong"
                description="We could not fetch cart"
                reset={refetch}
                buttonText="Try again"
              />
            ) : orders.item_count === 0 ? (
              <EmptyState text="There are no items in cart yet" />
            ) : (
              <div className="lg:hidden">
                {orders?.items?.map((item) => {
                  return (
                    <div
                      className="rounded-xl p-5 border border-default font-medium space-y-4  mb-5"
                      key={item.id}
                    >
                      <p>Payment ID - {item.id}</p>
                      <p>
                        Payment -{' '}
                        {formatCurrency(
                          item.total_shipping_cost
                            ? item.total_order_cost + item.total_shipping_cost
                            : item.total_order_cost
                        )}
                      </p>
                      <div className="justify-between flex items-center">
                        <div className="flex space-x-2 items-center">
                          <p>Status - </p>
                          <Chip
                            label={item?.request_status}
                            color={item.request_status === 'successful' ? 'success' : 'primary'}
                          />
                        </div>
                        <Button
                          variant="text"
                          onClick={() => {
                            if (props.next) {
                              props.next(item.id)
                            } else {
                              navigate(`cart/${item.id}`)
                            }
                          }}
                        >
                          View Details
                        </Button>
                      </div>
                    </div>
                  )
                })}

                {orders && (
                  <div className="justify-between text-neutral-500 mt-20 flex">
                    <Button
                      startIcon={<ArrowBackIcon />}
                      variant="outlined"
                      onClick={() => {
                        setPaginationModel({
                          ...paginationModel,
                          page: paginationModel.page - 1,
                        })
                      }}
                      disabled={paginationModel.page + 1 === 1}
                      className="px-3 border border-default text-neutral-500"
                    >
                      Previous
                    </Button>
                    <div className="page-number">
                      Page {paginationModel.page + 1} of{' '}
                      {Math.ceil(orders?.item_count / paginationModel.pageSize)}
                    </div>
                    <Button
                      endIcon={<ArrowForwardIcon className="text-error-900" />}
                      variant="outlined"
                      onClick={() => {
                        setPaginationModel({
                          ...paginationModel,
                          page: paginationModel.page + 1,
                        })
                      }}
                      disabled={
                        paginationModel.page + 1 ===
                        Math.ceil(orders?.item_count / paginationModel.pageSize)
                      }
                      className="px-3 py-1 border border-error-600 text-error-900"
                    >
                      Next
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        </Refresh>
        <div className="lg:block hidden overflow-hidden">
          <div className="rounded">
            {isLoading ? (
              <div className="grid place-items-center mt-12 mb-10">
                <CircularProgress />
              </div>
            ) : isError ? (
              <ErrorFallback
                message="Something went wrong"
                description="We could not fetch cart"
                reset={refetch}
                buttonText="Try again"
              />
            ) : orders?.item_count === 0 ? (
              <EmptyState text="There are no items in cart yet" />
            ) : (
              <div className="overflow-hidden">
                <DataGrid
                  rows={rows}
                  columns={columns}
                  rowCount={orders?.item_count || 0}
                  loading={isLoading}
                  paginationModel={paginationModel}
                  paginationMode="server"
                  onPaginationModelChange={setPaginationModel}
                  sx={{ '& .MuiDataGrid-columnSeparator': { display: 'none' } }}
                  slots={{
                    toolbar: () => (
                      <div className="flex justify-between py-8 items-center px-4">
                        <TextField
                          id="name"
                          type="text"
                          placeholder="Search"
                          className="w-1/2"
                          InputProps={{
                            sx: {
                              '& input': {
                                '&:focus': {
                                  backgroundColor: 'white',
                                },
                              },
                            },
                            className: 'p-0 overflow-hidden',
                            startAdornment: (
                              <Button className={`bg-white`}>
                                <Search className={`text-neutral-500 w-6 h-6`} />
                              </Button>
                            ),
                          }}
                        />
                        <Button
                          startIcon={<FilterListIcon />}
                          variant="outlined"
                          endIcon={<KeyboardArrowDownIcon />}
                          className="px-3 border border-default text-neutral-500"
                        >
                          Filter
                        </Button>
                      </div>
                    ),
                  }}
                  onRowClick={({ row }) => {
                    if (props.next) {
                      props.next(row.id)
                    } else {
                      navigate(`cart/${row.id}`)
                    }
                  }}
                  pageSizeOptions={[5, 10, 15, 20, 25]}
                  disableColumnMenu
                />
              </div>
            )}
          </div>
        </div>
      </PageTransition>
    </>
  )
}
